import React, { useState, useEffect, useMemo, useCallback } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import apiClient from "../../Utils/apiClient";

const PLANS_ENDPOINT = "/plans";

const subscriptionStyles = {
  PREMIUM: { color: "#6a1b9a", bg: "#f3e5f5" },
  STANDARD: { color: "#1565c0", bg: "#e3f2fd" },
};

const statusStyles = {
  ACTIVE: { color: "#2e7d32", bg: "#e8f5e9" },
  INACTIVE: { color: "#616161", bg: "#f5f5f5" },
};

const formatCurrency = (value) => {
  if (value === undefined || value === null || value === "") return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return `₦${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const SUBSCRIPTION_TYPES = ["Standard", "Premium"];
const PLAN_TYPES = ["Monthly", "Yearly"];

const emptyFeature = { title: "", description: "" };

const emptyForm = {
  name: "",
  subscriptionType: "Standard",
  planType: "Monthly",
  price: "",
  description: "",
  features: [{ ...emptyFeature }],
  isActive: true,
};

export default function SubscriptionsManagerPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [subTypeFilter, setSubTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [openMenuId, setOpenMenuId] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchPlans = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(PLANS_ENDPOINT);
      const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
      setRows(list);
    } catch (err) {
      toast.error(err?.message || "Failed to load plans");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  useEffect(() => {
    if (!openMenuId) return undefined;
    const close = () => setOpenMenuId(null);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [openMenuId]);

  const normalized = useMemo(() => {
    return rows.map((r, idx) => {
      const id = r._id || r.id || idx;
      const subscriptionType = r.subscriptionType || "Standard";
      const planType = r.planType || "Monthly";
      const status = r.isActive ? "ACTIVE" : "INACTIVE";
      return {
        raw: r,
        id,
        srNo: String(idx + 1).padStart(2, "0"),
        name: r.name || "—",
        subscriptionType,
        subscriptionBadge: subscriptionType.toUpperCase(),
        planType,
        price: formatCurrency(r.price),
        priceRaw: r.price,
        description: r.description || "",
        features: Array.isArray(r.features) ? r.features : [],
        isActive: Boolean(r.isActive),
        status,
      };
    });
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return normalized.filter((row) => {
      const matchSearch =
        !q ||
        (row.name || "").toLowerCase().includes(q) ||
        (row.subscriptionType || "").toLowerCase().includes(q) ||
        (row.planType || "").toLowerCase().includes(q) ||
        String(row.priceRaw ?? "").includes(q);
      const matchSub = !subTypeFilter || row.subscriptionBadge === subTypeFilter;
      const matchStatus = !statusFilter || row.status === statusFilter;
      return matchSearch && matchSub && matchStatus;
    });
  }, [normalized, search, subTypeFilter, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
  const currentPage = Math.min(page, totalPages);
  const pageRows = filtered.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  const stats = useMemo(() => {
    const total = normalized.length;
    const standardActive = normalized.filter((r) => r.subscriptionBadge === "STANDARD" && r.isActive).length;
    const premiumActive = normalized.filter((r) => r.subscriptionBadge === "PREMIUM" && r.isActive).length;
    const monthly = normalized.filter((r) => r.planType === "Monthly").length;
    const yearly = normalized.filter((r) => r.planType === "Yearly").length;
    const inactive = normalized.filter((r) => !r.isActive).length;
    return [
      { label: "TOTAL PLANS", value: total.toLocaleString(), icon: "📊", iconBg: "#e3f2fd" },
      { label: "STANDARD ACTIVE", value: standardActive.toLocaleString(), icon: "✅", iconBg: "#e8f5e9", valueColor: "#2e7d32" },
      { label: "PREMIUM ACTIVE", value: premiumActive.toLocaleString(), icon: "⭐", iconBg: "#f3e5f5", valueColor: "#6a1b9a" },
      { label: "MONTHLY PLANS", value: monthly.toLocaleString(), icon: "📅", iconBg: "#fff3e0", valueColor: "#e65100" },
      { label: "YEARLY PLANS", value: yearly.toLocaleString(), icon: "🗓️", iconBg: "#e8eaf6", valueColor: "#3949ab" },
      { label: "INACTIVE PLANS", value: inactive.toLocaleString(), icon: "⚠️", iconBg: "#fce4ec", valueColor: "#c62828" },
    ];
  }, [normalized]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    const r = row.raw || {};
    const features = Array.isArray(r.features) && r.features.length
      ? r.features.map((f) => ({ title: f?.title || "", description: f?.description || "" }))
      : [{ ...emptyFeature }];
    setEditingId(row.id);
    setForm({
      name: r.name || "",
      subscriptionType: r.subscriptionType || "Standard",
      planType: r.planType || "Monthly",
      price: r.price ?? "",
      description: r.description || "",
      features,
      isActive: r.isActive !== false,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
    setEditingId(null);
    setForm(emptyForm);
  };

  const handleFormChange = (field) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleFeatureChange = (index, field) => (e) => {
    const { value } = e.target;
    setForm((prev) => {
      const features = prev.features.map((f, i) => (i === index ? { ...f, [field]: value } : f));
      return { ...prev, features };
    });
  };

  const handleAddFeature = () => {
    setForm((prev) => ({ ...prev, features: [...prev.features, { ...emptyFeature }] }));
  };

  const handleRemoveFeature = (index) => {
    setForm((prev) => {
      const features = prev.features.filter((_, i) => i !== index);
      return { ...prev, features: features.length ? features : [{ ...emptyFeature }] };
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Plan name is required");
      return;
    }
    if (form.price === "" || Number(form.price) < 0) {
      toast.error("Valid price is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        subscriptionType: form.subscriptionType,
        planType: form.planType,
        price: Number(form.price),
        description: form.description.trim(),
        features: form.features
          .map((f) => ({
            title: (f.title || "").trim(),
            description: (f.description || "").trim(),
          }))
          .filter((f) => f.title || f.description),
        isActive: Boolean(form.isActive),
      };
      if (editingId) {
        await apiClient.put(`${PLANS_ENDPOINT}/${editingId}`, payload);
        toast.success("Plan updated");
      } else {
        await apiClient.post(PLANS_ENDPOINT, payload);
        toast.success("Plan created");
      }
      closeModal();
      fetchPlans();
    } catch (err) {
      toast.error(err?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete plan "${row.name}"?`)) return;
    try {
      await apiClient.delete(`${PLANS_ENDPOINT}/${row.id}`);
      toast.success("Plan deleted");
      fetchPlans();
    } catch (err) {
      toast.error(err?.message || "Delete failed");
    }
  };

  const handleExport = () => {
    if (!filtered.length) {
      toast.info("Nothing to export");
      return;
    }
    const headers = ["Name", "Subscription Type", "Plan Type", "Price", "Status"];
    const csv = [
      headers.join(","),
      ...filtered.map((r) =>
        [r.name, r.subscriptionType, r.planType, r.priceRaw, r.status]
          .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`)
          .join(",")
      ),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `plans-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <DashboardLayout>
      <div style={{ paddingTop: 64, fontFamily: "'Segoe UI', Roboto, sans-serif" }}>
        <ToastContainer position="top-right" autoClose={2500} />

      {/* Banner */}
      <div style={{
        background: "linear-gradient(135deg, #1565c0 0%, #1976d2 60%, #1e88e5 100%)",
        borderRadius: 12,
        padding: "22px 28px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        color: "#fff",
        marginBottom: 16,
        flexWrap: "wrap",
        gap: 16,
      }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>Subscriptions Manager</h1>
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ backgroundColor: "rgba(255,255,255,0.15)", borderRadius: 10, padding: "10px 20px", textAlign: "center" }}>
            <span style={{ fontSize: 20, fontWeight: 700, display: "block" }}>{normalized.length.toLocaleString()}</span>
            <span style={{ fontSize: 10, opacity: 0.85, textTransform: "uppercase", letterSpacing: 0.5 }}>Total Plans</span>
          </div>
          <div style={{ backgroundColor: "rgba(255,255,255,0.2)", borderRadius: 10, padding: "10px 20px", textAlign: "center" }}>
            <span style={{ fontSize: 20, fontWeight: 700, display: "block", color: "#69f0ae" }}>
              {normalized.length
                ? `${Math.round((normalized.filter((r) => r.isActive).length / normalized.length) * 1000) / 10}%`
                : "0%"}
            </span>
            <span style={{ fontSize: 10, opacity: 0.85, textTransform: "uppercase", letterSpacing: 0.5 }}>Active Rate</span>
          </div>
          <button
            onClick={openCreate}
            style={{ backgroundColor: "#fff", color: "#1565c0", border: "none", borderRadius: 8, padding: "10px 16px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}
          >
            + New Plan
          </button>
        </div>
      </div>

      {/* Stat Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 12, marginBottom: 16 }}>
        {stats.map((s, i) => (
          <div key={i} style={{
            backgroundColor: "#fff",
            borderRadius: 10,
            padding: "14px 16px",
            boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
          }}>
            <div style={{
              width: 36, height: 36, borderRadius: 8,
              backgroundColor: s.iconBg,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 16, marginBottom: 8,
            }}>{s.icon}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: s.valueColor || "#1a2035", lineHeight: 1 }}>{s.value}</div>
            <div style={{ fontSize: 9, color: "#999", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, marginTop: 4 }}>{s.label}</div>
          </div>
        ))}
      </div>

      <div style={{ backgroundColor: "#fff", borderRadius: 10, boxShadow: "0 1px 4px rgba(0,0,0,0.06)", marginBottom: 16 }}>

        {/* Filters */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 20px", borderBottom: "1px solid #f0f0f0", flexWrap: "wrap" }}>
          <input
            style={{ flex: 1, minWidth: 200, border: "1px solid #e0e0e0", borderRadius: 6, padding: "7px 12px", fontSize: 12, outline: "none", color: "#333" }}
            placeholder="🔍  Search by Name, Type, Price..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
          <select
            style={{ border: "1px solid #e0e0e0", borderRadius: 6, padding: "7px 12px", fontSize: 12, color: "#555", backgroundColor: "#fff", outline: "none", cursor: "pointer" }}
            value={subTypeFilter}
            onChange={(e) => { setSubTypeFilter(e.target.value); setPage(1); }}
          >
            <option value="">Subscription Type</option>
            <option value="STANDARD">Standard</option>
            <option value="PREMIUM">Premium</option>
          </select>
          <select
            style={{ border: "1px solid #e0e0e0", borderRadius: 6, padding: "7px 12px", fontSize: 12, color: "#555", backgroundColor: "#fff", outline: "none", cursor: "pointer" }}
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          >
            <option value="">Status</option>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
          </select>
          <button
            onClick={handleExport}
            style={{ backgroundColor: "#1a2035", color: "#fff", border: "none", borderRadius: 6, padding: "7px 16px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
          >
            ▲ Export
          </button>
        </div>

        {/* Table Title + Rows per page */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 20px 8px" }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: "#1a2035" }}>Plan Management</span>
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#777" }}>
            Rows per page:
            <select
              value={rowsPerPage}
              onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(1); }}
              style={{ border: "1px solid #e0e0e0", borderRadius: 6, padding: "4px 8px", fontSize: 12, color: "#555", backgroundColor: "#fff" }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
          <thead>
            <tr>
              {["SR", "Name", "Subscription Type", "Plan Type", "Price", "Status", "Action"].map((h) => (
                <th key={h} style={{
                  fontSize: 10, fontWeight: 700, color: "#999",
                  textTransform: "uppercase", letterSpacing: 0.5,
                  padding: "8px 12px", textAlign: "left",
                  backgroundColor: "#fafafa", borderBottom: "1px solid #f0f0f0",
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ padding: 24, textAlign: "center", color: "#888", fontSize: 13 }}>Loading plans...</td></tr>
            ) : pageRows.length === 0 ? (
              <tr><td colSpan={7} style={{ padding: 24, textAlign: "center", color: "#888", fontSize: 13 }}>No plans found</td></tr>
            ) : (
              pageRows.map((row) => {
                const sub = subscriptionStyles[row.subscriptionBadge] || subscriptionStyles.STANDARD;
                const st = statusStyles[row.status] || statusStyles.ACTIVE;
                return (
                  <tr key={row.id}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#fafafa")}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "")}
                  >
                    <td style={{ padding: "12px", fontSize: 12, color: "#555", borderBottom: "1px solid #f8f8f8" }}>{row.srNo}</td>
                    <td style={{ padding: "12px", fontSize: 12, color: "#1a2035", fontWeight: 600, borderBottom: "1px solid #f8f8f8" }}>{row.name}</td>
                    <td style={{ padding: "12px", borderBottom: "1px solid #f8f8f8" }}>
                      <span style={{
                        fontSize: 10, fontWeight: 700, borderRadius: 4, padding: "3px 8px", letterSpacing: 0.5,
                        backgroundColor: sub.bg, color: sub.color,
                      }}>{row.subscriptionType}</span>
                    </td>
                    <td style={{ padding: "12px", fontSize: 12, color: "#1a2035", fontWeight: 500, borderBottom: "1px solid #f8f8f8" }}>{row.planType}</td>
                    <td style={{ padding: "12px", fontSize: 12, fontWeight: 600, color: "#1a2035", borderBottom: "1px solid #f8f8f8" }}>{row.price}</td>
                    <td style={{ padding: "12px", borderBottom: "1px solid #f8f8f8" }}>
                      <span style={{
                        fontSize: 10, fontWeight: 700, borderRadius: 12, padding: "4px 10px", textTransform: "uppercase",
                        backgroundColor: st.bg, color: st.color,
                      }}>{row.status}</span>
                    </td>
                    <td style={{ padding: "12px", borderBottom: "1px solid #f8f8f8", position: "relative" }}>
                      <button
                        onClick={(e) => { e.stopPropagation(); setOpenMenuId(openMenuId === row.id ? null : row.id); }}
                        style={{ border: "none", backgroundColor: "transparent", fontSize: 18, cursor: "pointer", color: "#bbb" }}
                      >⋮</button>
                      {openMenuId === row.id && (
                        <div
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            position: "absolute", right: 12, top: 36, zIndex: 20,
                            backgroundColor: "#fff", border: "1px solid #e0e0e0", borderRadius: 6,
                            boxShadow: "0 4px 12px rgba(0,0,0,0.08)", minWidth: 120,
                          }}
                        >
                          <button
                            onClick={() => { setOpenMenuId(null); openEdit(row); }}
                            style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 12px", border: "none", backgroundColor: "transparent", fontSize: 12, cursor: "pointer", color: "#1a2035" }}
                          >✏️  Edit</button>
                          <button
                            onClick={() => { setOpenMenuId(null); handleDelete(row); }}
                            style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 12px", border: "none", backgroundColor: "transparent", fontSize: 12, cursor: "pointer", color: "#c62828" }}
                          >🗑️  Delete</button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        </div>

        {/* Pagination */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 20px", borderTop: "1px solid #f0f0f0" }}>
          <span style={{ fontSize: 12, color: "#888" }}>
            {filtered.length === 0
              ? "Showing 0 plans"
              : `Showing ${(currentPage - 1) * rowsPerPage + 1}-${Math.min(currentPage * rowsPerPage, filtered.length)} of ${filtered.length} plans`}
          </span>
          <div style={{ display: "flex", gap: 4 }}>
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              style={{ width: 28, height: 28, borderRadius: 6, border: "1px solid #e0e0e0", backgroundColor: "#fff", color: "#555", fontSize: 12, cursor: currentPage === 1 ? "not-allowed" : "pointer" }}
            >‹</button>
            {Array.from({ length: totalPages }).slice(0, 5).map((_, i) => {
              const p = i + 1;
              const active = p === currentPage;
              return (
                <button key={p}
                  onClick={() => setPage(p)}
                  style={{
                    width: 28, height: 28, borderRadius: 6,
                    border: active ? "1px solid #1976d2" : "1px solid #e0e0e0",
                    backgroundColor: active ? "#1976d2" : "#fff",
                    color: active ? "#fff" : "#555",
                    fontSize: 12, cursor: "pointer",
                  }}>{p}</button>
              );
            })}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              style={{ width: 28, height: 28, borderRadius: 6, border: "1px solid #e0e0e0", backgroundColor: "#fff", color: "#555", fontSize: 12, cursor: currentPage === totalPages ? "not-allowed" : "pointer" }}
            >›</button>
          </div>
        </div>
      </div>

      {/* Create / Edit Modal */}
      {modalOpen && (
        <div
          onClick={closeModal}
          style={{
            position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
          }}
        >
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={handleSave}
            style={{
              backgroundColor: "#fff", borderRadius: 12, width: 560, maxWidth: "95%",
              boxShadow: "0 12px 40px rgba(0,0,0,0.2)", overflow: "hidden",
            }}
          >
            <div style={{ padding: "16px 20px", borderBottom: "1px solid #f0f0f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h3 style={{ margin: 0, fontSize: 16, color: "#1a2035" }}>{editingId ? "Edit Plan" : "New Plan"}</h3>
              <button type="button" onClick={closeModal} style={{ border: "none", background: "transparent", fontSize: 20, cursor: "pointer", color: "#888" }}>×</button>
            </div>
            <div style={{ padding: 20, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div style={{ gridColumn: "1 / -1" }}>
                <FormField label="Plan Name *" value={form.name} onChange={handleFormChange("name")} />
              </div>
              <FormSelect label="Subscription Type *" value={form.subscriptionType} onChange={handleFormChange("subscriptionType")} options={SUBSCRIPTION_TYPES} />
              <FormSelect label="Plan Type *" value={form.planType} onChange={handleFormChange("planType")} options={PLAN_TYPES} />
              <div style={{ gridColumn: "1 / -1" }}>
                <FormField label="Price *" type="number" value={form.price} onChange={handleFormChange("price")} />
              </div>
              <div style={{ gridColumn: "1 / -1", display: "flex", flexDirection: "column", gap: 8 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 11, color: "#555", fontWeight: 600 }}>Features</span>
                  <button
                    type="button"
                    onClick={handleAddFeature}
                    style={{ border: "1px solid #1976d2", color: "#1976d2", backgroundColor: "#fff", borderRadius: 6, padding: "4px 10px", fontSize: 11, fontWeight: 600, cursor: "pointer" }}
                  >
                    + Add Feature
                  </button>
                </div>
                {form.features.map((f, i) => (
                  <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1.5fr auto", gap: 8, alignItems: "start" }}>
                    <input
                      type="text"
                      placeholder="Title"
                      value={f.title}
                      onChange={handleFeatureChange(i, "title")}
                      style={{ border: "1px solid #e0e0e0", borderRadius: 6, padding: "8px 10px", fontSize: 12, outline: "none", color: "#1a2035" }}
                    />
                    <input
                      type="text"
                      placeholder="Description"
                      value={f.description}
                      onChange={handleFeatureChange(i, "description")}
                      style={{ border: "1px solid #e0e0e0", borderRadius: 6, padding: "8px 10px", fontSize: 12, outline: "none", color: "#1a2035" }}
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveFeature(i)}
                      title="Remove feature"
                      style={{ border: "1px solid #e0e0e0", backgroundColor: "#fff", color: "#c62828", borderRadius: 6, padding: "0 10px", fontSize: 14, cursor: "pointer", height: 34 }}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#555", fontWeight: 600, gridColumn: "1 / -1" }}>
                <input
                  type="checkbox"
                  checked={Boolean(form.isActive)}
                  onChange={handleFormChange("isActive")}
                />
                Active
              </label>
            </div>
            <div style={{ padding: "12px 20px", borderTop: "1px solid #f0f0f0", display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button type="button" onClick={closeModal} disabled={saving}
                style={{ padding: "8px 16px", border: "1px solid #e0e0e0", borderRadius: 6, backgroundColor: "#fff", color: "#555", fontSize: 12, cursor: "pointer" }}>
                Cancel
              </button>
              <button type="submit" disabled={saving}
                style={{ padding: "8px 20px", border: "none", borderRadius: 6, backgroundColor: "#1976d2", color: "#fff", fontSize: 12, fontWeight: 600, cursor: saving ? "wait" : "pointer" }}>
                {saving ? "Saving..." : editingId ? "Update" : "Create"}
              </button>
            </div>
          </form>
        </div>
      )}
      </div>
    </DashboardLayout>
  );
}

function FormField({ label, value, onChange, type = "text" }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, color: "#555", fontWeight: 600 }}>
      {label}
      <input
        type={type}
        value={value}
        onChange={onChange}
        style={{ border: "1px solid #e0e0e0", borderRadius: 6, padding: "8px 10px", fontSize: 12, outline: "none", color: "#1a2035", fontWeight: 400 }}
      />
    </label>
  );
}

function FormSelect({ label, value, onChange, options }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, color: "#555", fontWeight: 600 }}>
      {label}
      <select
        value={value}
        onChange={onChange}
        style={{ border: "1px solid #e0e0e0", borderRadius: 6, padding: "8px 10px", fontSize: 12, outline: "none", color: "#1a2035", fontWeight: 400, backgroundColor: "#fff" }}
      >
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}
